import {MolecularGraph} from "../../graph"
import {carbonylCoreExtractor} from "./carbonylCoreExtractor";
import {Feature, Features} from "../../feature";
import {halogen_elements} from "./halogens";
import {extractorUtil} from "../../util/extractorUtil";

// Joe and Sarah 07/09/2025 R is in the sketcher vernacular and should be treated like a "C"
const carbonyl_like = ["C", "O", "H", "N", ...halogen_elements, "R"]

export const carbonylExtractor = {
    extract: (graph: MolecularGraph) => {
        const roots = carbonylCoreExtractor.extract(graph);
        const features: Feature[] = [];
        while(roots.length > 0) {
            const root = roots.pop()!;

            if(root.connections.length === 1) {
                // Hs get filtered out as part of the rGroups.
                // aldehydes are tricky because we actually need the H.
                // So, if there's only 1 rGroup, it has to be a C.
                // If it actually has another H attached to it, then
                // it's not a carbonyl, but an aldehyde.
                // Use total H count to handle both explicit and implicit hydrogens
                const totalHCount = extractorUtil.getTotalHydrogenCount(root.atoms[0], graph);
                if(totalHCount === 0) {
                    // Only one connection? we're good
                    features.push(root);
                } else if(totalHCount === 1) {
                    // It's an aldehyde
                    // The connection point is actually the carbon of the aldehyde.
                    root.connections = [root.atoms[0]]
                    root.name = Features.aldehyde;
                    features.push(root);
                }
            } else if(!carbonyl_like.includes(root.connections[1].symbol)) {
                // If the second R is not a carbonyl-like
                features.push(root);
            } else if(root.connections[1].symbol !== "O") { // Esters and variants are handled separately
                const otherSymbol = root.connections[1].symbol;
                // It's a special class of carbonyl.
                if(halogen_elements.includes(otherSymbol)) {
                    // Push the halide atom in to the group
                    // reconfigure the R groups.
                    const halide = root.connections[1];
                    root.atoms.push(halide);
                    root.connections = [root.atoms[0], ...extractorUtil.getFilteredNonHConnectedAtoms(halide, graph, root.atoms)];
                    root.name = extractorUtil.getAcidHalideNameFor(halide.symbol);
                    features.push(root);
                } else if(otherSymbol === "C") {
                    // Move the central C to the connections.
                    root.connections = [root.atoms[0]]
                    root.name = Features.ketone;
                    features.push(root);
                } else if(otherSymbol === "N") {
                    root.name = Features.amide;
                    // The nitrogen gets pulled into the group atoms
                    // and the R groups are reconfigured
                    const n = root.connections[1];
                    root.atoms.push(n);

                    // Trim the N from the r groups.
                    root.connections.splice(1, 1);

                    // Add the new R groups from N
                    const newR = extractorUtil.getFilteredNonHConnectedAtoms(n, graph, root.atoms)

                    // The R groups from N determine the amide name. (amide.1, amide.2, amide.3)
                    root.name += "." + (newR.length + 1);

                    // Move the central atom to the top of the connections pile.
                    root.connections = [root.atoms[0]]
                    features.push(root);
                }
            } else {
                const ester = carbonylExtractor.buildEster(root, graph);
                features.push(ester);
            }
        }

        // The last feature is an acid anhydride. It's composed of 2 esters that share a common "O"
        return carbonylExtractor.combineEsters(features);
    },
    combineEsters: (features: Feature[]) => {
        // You combine them together and the R groups are the R groups attached to the original
        // groups. NOTE: One of the end C's in each ester is part of the carbonyl core so the final
        // R groups are the Cs on the ends.
        const esters = features.filter(f => f.name == Features.ester);
        const allFeatures = features.filter(f => !esters.includes(f));

        while(esters.length > 0) {
            const ester = esters.pop()!;

            // Fragile? We build an ester a certain way. We could just check the
            // 3rd atom to see if it matches any other ester
            const partners = esters.filter(f => f.atoms[2] === ester.atoms[2]);
            if(partners.length > 0) {
                // We ignore multiple partners and just pick one of them as the partner to make the anhydride
                const partner = partners[0];

                // Remove the partner ester
                esters.splice(esters.indexOf(partner), 1);

                // combine the esters into an acid anydride
                ester.name = Features.anhydride;
                ester.atoms = Array.from(new Set([...ester.atoms, ...partner.atoms]));
                ester.connections =[ester.atoms[0], partner.atoms[0]]

            }

            allFeatures.push(ester);
        }
        return allFeatures;
    },
    buildEster: (root: Feature, graph: MolecularGraph) => {
        const o = root.connections[1];
        const bondsFromO = graph.getBondsAttachedTo(o);
        const hBonds = bondsFromO.filter(b => extractorUtil.isSingleBondToHFrom(b, o));
        const cBonds = bondsFromO.filter(b => extractorUtil.isSingleBondToCFrom(b, o));
        // esters have priority even if you can find a carboxylic acid, they prefer you find
        // the ester first.
        if(cBonds.length === 2) {
            // Any O-C is considered an ester. O can have other things attached.
            // The C is now part of the R group and the O is part of the functional group.
            root.name = Features.ester;

            // put the O in the group
            root.atoms.push(o);

            root.connections = [root.atoms[0]];

        } else if(cBonds.length === 1 && extractorUtil.getTotalHydrogenCount(o, graph) >= 1) {
            // carboxylic acid - O has exactly one C bond and at least one H (explicit or implicit)
            root.name = Features.carbAcid;

            // Add O to the group, and explicit H atoms if they exist
            root.atoms.push(o);
            if (hBonds.length >= 1) {
                root.atoms.push(hBonds[0].getOtherAtom(o));
            }

            root.connections = [root.atoms[0]];
        }

        return root;

    }
}
